/**
 * Console module registry — the single source of truth for the operator
 * console's 11 SRS modules, their routes, sidebar labels, and one-line SRS
 * descriptions.
 *
 * Both the shell navigation and the routing/screen selection read this table so
 * the sidebar and the rendered screen never drift. Each module maps a URL path
 * to a stable {@link ModuleKey}; the shell renders the flagship screens for
 * `dashboard` and `properties`, and a professional placeholder for the rest.
 */

/** Stable identifiers for the console modules. */
export type ModuleKey =
  | "dashboard"
  | "properties"
  | "bookings"
  // "booking" (Direct Booking) merged into "bookings"; kept for redirect fallback.
  | "booking"
  | "inventory"
  | "channels"
  | "reconciliation"
  | "revenue"
  | "crm"
  | "automation"
  | "marketing"
  | "operations"
  | "agents"
  | "analytics"
  | "accounts"
  | "admin";

/** A single navigable console module. */
export interface ConsoleModule {
  /** Stable key identifying which screen renders. */
  readonly key: ModuleKey;
  /** URL path (exact match against the request pathname). */
  readonly path: string;
  /** Sidebar navigation label. */
  readonly label: string;
  /** One-line description sourced from the SRS. */
  readonly description: string;
}

/** The console's modules, in sidebar display order (all 11 SRS modules). */
export const CONSOLE_MODULES: readonly ConsoleModule[] = [
  {
    key: "dashboard",
    path: "/app/dashboard",
    label: "Dashboard",
    description:
      "At-a-glance operational metrics across your portfolio: inventory, capacity, and rate performance.",
  },
  {
    key: "properties",
    path: "/app/properties",
    label: "Properties",
    description:
      "Manage properties, units, rate plans, and day-level availability for your portfolio.",
  },
  {
    key: "bookings",
    path: "/app/bookings",
    label: "Bookings",
    description:
      "Every reservation across direct and OTA channels, with filters, payment status, and booking history.",
  },
  {
    key: "inventory",
    path: "/app/inventory",
    label: "Inventory & Rates",
    description:
      "Inventory, the rate calendar, rate plans, restrictions and pricing rules for your portfolio.",
  },
  {
    key: "channels",
    path: "/app/channels",
    label: "Channel Manager",
    description:
      "Two-way sync of rates and availability with OTAs to prevent double bookings.",
  },
  {
    key: "reconciliation",
    path: "/app/reconciliation",
    label: "Reconciliation",
    description:
      "Match payouts and bookings across channels to keep your financial records accurate.",
  },
  {
    key: "revenue",
    path: "/app/revenue",
    label: "Revenue",
    description:
      "Portfolio business performance: gross revenue, occupancy, and per-property results.",
  },
  {
    key: "crm",
    path: "/app/crm",
    label: "Guest CRM",
    description:
      "A unified guest profile with stay history, preferences, and consent-aware communications.",
  },
  {
    key: "automation",
    path: "/app/automation",
    label: "Automation",
    description:
      "Trigger-based workflows for messaging, tasks, and operations across the guest lifecycle.",
  },
  {
    key: "marketing",
    path: "/app/marketing",
    label: "Marketing & SEO",
    description:
      "Campaigns, landing pages, and SEO tooling to grow direct demand for your properties.",
  },
  {
    key: "operations",
    path: "/app/operations",
    label: "Operations",
    description:
      "Housekeeping, maintenance, and task management to keep every unit guest-ready.",
  },
  {
    key: "agents",
    path: "/app/agents",
    label: "AI Agents",
    description:
      "Autonomous agents with governance guardrails: autonomy levels, a global kill switch, confidence thresholds, and a full action log (SRS §9).",
  },
  {
    key: "analytics",
    path: "/app/analytics",
    label: "Analytics",
    description:
      "Cross-module reporting on occupancy, revenue, and channel performance over time.",
  },
  {
    key: "accounts",
    path: "/app/accounts",
    label: "Accounts",
    description:
      "Stays & invoices, expenses, cash & banks, vouchers, owner statements and the GST ledger — the property's books.",
  },
  {
    key: "admin",
    path: "/app/settings",
    label: "Admin & Settings",
    description:
      "Team roles, tenant configuration, integrations, and account settings.",
  },
] as const;

/** The app namespace root; bare `/app` resolves to the Dashboard. */
export const APP_ROOT_PATH = "/app";

/** Stable identifiers for the authenticated account/settings sub-screens. */
export type SettingsSectionKey =
  | "profile"
  | "security"
  | "team"
  | "users"
  | "support-access"
  | "data-privacy";

/** A settings sub-section rendered inside the app shell under /app/settings/*. */
export interface SettingsSection {
  readonly key: SettingsSectionKey;
  readonly path: string;
  readonly label: string;
}

/**
 * The authenticated account functions, grouped under /app/settings/*. Each
 * reuses an existing account screen (no new screens): profile → Account,
 * team → Role management, support-access → Impersonation consent, data-privacy
 * → Export + Delete, security → MFA enrolment.
 */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  { key: "profile", path: "/app/settings/profile", label: "Profile" },
  { key: "security", path: "/app/settings/security", label: "Security" },
  { key: "users", path: "/app/settings/users", label: "Users & Access" },
  { key: "team", path: "/app/settings/team", label: "Team" },
  {
    key: "support-access",
    path: "/app/settings/support-access",
    label: "Support access",
  },
  {
    key: "data-privacy",
    path: "/app/settings/data-privacy",
    label: "Data & privacy",
  },
] as const;

/** Resolve a settings sub-section from a pathname, or null when not one. */
export function matchSettingsSection(pathname: string): SettingsSection | null {
  const normalized =
    pathname.split(/[?#]/)[0]?.replace(/\/+$/, "") ?? "";
  return SETTINGS_SECTIONS.find((s) => s.path === normalized) ?? null;
}

/** The Dashboard module path (the app's default landing screen). */
export const APP_DASHBOARD_PATH = "/app/dashboard";

/**
 * Resolve a request pathname to a console module. Falls back to the Dashboard
 * ("/app/dashboard") so the shell always renders a valid module. Nested paths
 * under a module (e.g. the settings sub-sections "/app/settings/profile")
 * resolve to their parent module (Admin & Settings) by longest-prefix match.
 */
export function matchModule(pathname: string): ConsoleModule {
  const normalized = normalizePathname(pathname);
  // Bare "/app" → Dashboard.
  if (normalized === APP_ROOT_PATH) {
    return CONSOLE_MODULES[0] as ConsoleModule;
  }
  const exact = CONSOLE_MODULES.find((m) => m.path === normalized);
  if (exact !== undefined) return exact;
  // Longest-prefix match so sub-routes (e.g. "/app/settings/profile") map to
  // their parent module. Guarded with a "/" boundary so "/app/accounts" never
  // matches "/app/accountsX".
  const prefixed = [...CONSOLE_MODULES]
    .filter((m) => normalized.startsWith(`${m.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return prefixed ?? (CONSOLE_MODULES[0] as ConsoleModule);
}

/** Normalize a pathname: strip query/hash and any trailing slash (except root). */
function normalizePathname(pathname: string): string {
  const withoutQuery = pathname.split(/[?#]/)[0] ?? APP_DASHBOARD_PATH;
  if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery === "" ? APP_DASHBOARD_PATH : withoutQuery;
}

/**
 * The permission that gates each module in the sidebar + route guard. A user
 * sees/reaches a module only when they hold its permission. Admin (settings)
 * is always visible (personal profile/security are available to everyone);
 * the Users & Access sub-tabs are gated separately by users.view/roles.view.
 */
export const MODULE_PERMISSION: Readonly<Record<ModuleKey, string | null>> = {
  dashboard: "dashboard.view",
  properties: "properties.view",
  bookings: "bookings.view",
  booking: "bookings.view",
  inventory: "properties.view",
  channels: "channels.view",
  reconciliation: "reconciliation.view",
  revenue: "revenue.view",
  crm: "crm.view",
  automation: "operations.view",
  marketing: "marketing.view",
  operations: "operations.view",
  agents: "settings.manage",
  analytics: "analytics.view",
  accounts: "accounts.view",
  admin: null, // always available (personal settings); sub-tabs gated separately
};

/** Ordered fallback for a user's landing module when Dashboard is not permitted. */
export const LANDING_ORDER: readonly ModuleKey[] = [
  "dashboard", "operations", "bookings", "inventory", "accounts", "revenue",
  "crm", "channels", "analytics", "properties", "marketing", "admin",
];

